import { Footer, Head, Loader, Nav, Social, Email } from '@components';
import { GlobalStyle, theme } from '@styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import styled, { ThemeProvider, keyframes } from 'styled-components';

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const StyledCursor = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle at center, rgba(255, 42, 42, 0.4) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 9998;
  will-change: transform, opacity;
  transition: opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), width 0.4s ease, height 0.4s ease;
  opacity: ${props => (props.active ? 1 : 0)};

  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
    background: radial-gradient(circle at center, rgba(255, 42, 42, 0.35) 0%, transparent 70%);
  }
`;

const fogDrift1 = keyframes`
  0% { transform: translate(0, 0) scale(1) rotate(0deg); }
  50% { transform: translate(40vw, -20vh) scale(1.3) rotate(180deg); }
  100% { transform: translate(-10vw, 40vh) scale(0.8) rotate(360deg); }
`;

const fogDrift2 = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(70vw, 70vh) rotate(90deg); }
  50% { transform: translate(70vw, 0vh) rotate(180deg); }
  75% { transform: translate(0vw, 70vh) rotate(270deg); }
  100% { transform: translate(0, 0) rotate(360deg); }
`;

const fogDrift3 = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(-60vw, -60vh) rotate(-90deg); }
  50% { transform: translate(-60vw, 0vh) rotate(-180deg); }
  75% { transform: translate(0vw, -60vh) rotate(-270deg); }
  100% { transform: translate(0, 0) rotate(-360deg); }
`;

const fogRoll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const StyledFogLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.05) 20%,
    rgba(255, 42, 42, 0.05) 40%,
    rgba(29, 78, 216, 0.05) 60%,
    rgba(255, 255, 255, 0.05) 80%,
    transparent 100%
  );
  filter: blur(50px);
  pointer-events: none;
  z-index: -1;
  animation: ${fogRoll} ${props => props.duration || '30s'} linear infinite;
  opacity: ${props => props.opacity || '1'};

  @media (hover: none), (pointer: coarse) {
    display: none !important;
  }
`;

const StyledFogCloud = styled.div`
  position: fixed;
  width: ${props => props.size || '500px'};
  height: ${props => props.size || '500px'};
  background: ${props => props.color || 'rgba(255, 255, 255, 0.3)'};
  filter: blur(30px) url(#fog-filter);
  border-radius: 40% 60% 60% 40% / 40% 40% 60% 60%;
  pointer-events: none;
  z-index: -1;
  top: ${props => props.top || '0'};
  left: ${props => props.left || '0'};
  will-change: transform;
  animation: ${props => props.animation || fogDrift1} ${props => props.duration || '20s'} linear
    infinite;
  animation-delay: ${props => props.delay || '0s'};

  @media (hover: none), (pointer: coarse) {
    animation: none !important;
    filter: blur(55px) !important;
    opacity: 0.55 !important;
    z-index: -1 !important;

    &::before,
    &::after {
      content: '';
      position: absolute;
      background: inherit;
      pointer-events: none;
      opacity: 0.9;
    }

    &::before {
      width: 70%;
      height: 80%;
      top: -15%;
      left: 15%;
      border-radius: 60% 40% 50% 50% / 50% 60% 40% 50%;
    }

    &::after {
      width: 80%;
      height: 70%;
      bottom: -10%;
      left: 5%;
      border-radius: 40% 60% 50% 50% / 60% 40% 50% 50%;
    }

    &.fog-cloud-red {
      width: 120% !important;
      height: 300px !important;
      top: 10% !important;
      left: -10% !important;
      background: rgba(255, 42, 42, 0.3) !important;
    }

    &.fog-cloud-blue {
      width: 130% !important;
      height: 320px !important;
      top: 52% !important;
      left: -15% !important;
      background: rgba(29, 78, 216, 0.3) !important;
    }
  }
`;

const StyledBackgroundWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

let hasLoadedInitially = false;

const Layout = ({ children, location }) => {
  const isHome = location.pathname === '/';
  const [isLoading, setIsLoading] = useState(
    () =>
      // If we've already run the loader in the current session, skip it on client-side route changes
      !hasLoadedInitially,
  );
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });

  const [isCursorActive, setIsCursorActive] = useState(false);
  const isDragging = useRef(false);

  // Note: avoid forcing hard redirects during render; Gatsby's router + browser history
  // already handle back/forward and navigation reliably.

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleMouseMove = e => {
      setCursorPos({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
    };

    const handleMouseLeave = () => {
      setIsCursorActive(false);
    };

    const handleMouseEnter = () => {
      setIsCursorActive(true);
    };

    const handleTouchStart = e => {
      isDragging.current = false;
      if (e.touches && e.touches[0]) {
        setCursorPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
        setIsCursorActive(true);
      }
    };

    const handleTouchMove = e => {
      isDragging.current = true;
      if (e.touches && e.touches[0]) {
        setCursorPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      }
    };

    const handleTouchEnd = () => {
      if (isDragging.current) {
        setIsCursorActive(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  // Sets target="_blank" rel="noopener noreferrer" on external links
  const handleExternalLinks = () => {
    const allLinks = Array.from(document.querySelectorAll('a'));
    if (allLinks.length > 0) {
      allLinks.forEach(link => {
        // Skip mailto: and tel: links — they aren't real external links
        if (link.protocol === 'mailto:' || link.protocol === 'tel:') {
          return;
        }
        if (link.hostname !== window.location.hostname) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        }
      });
    }
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (location.hash) {
      const id = location.hash.substring(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView();
          el.focus();
        }
      }, 0);
    }
    handleExternalLinks();
  }, [isLoading]);

  useEffect(() => {
    const handleContextmenu = e => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextmenu);
    return function cleanup() {
      document.removeEventListener('contextmenu', handleContextmenu);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleTouchStart = e => {
      const target = e.target.closest('a, button');
      if (target) {
        target.classList.add('touch-active');
      }
    };

    const handleTouchEnd = e => {
      const target = e.target.closest('a, button');
      if (target) {
        const duration = parseInt(target.getAttribute('data-touch-duration'), 10) || 150;
        setTimeout(() => {
          target.classList.remove('touch-active');
        }, duration);
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  return (
    <>
      <Head />
      <div id="root">
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <svg style={{ position: 'absolute', width: 0, height: 0 }}>
            <filter id="fog-filter">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.015"
                numOctaves="3"
                result="noise"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="noise"
                scale="60"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </svg>

          <StyledBackgroundWrapper>
            <StyledFogLayer duration="80s" opacity="1.5" />
            <StyledFogLayer duration="60s" opacity="1.5" style={{ top: '30%', height: '70%' }} />
            <StyledFogCloud
              size="600px"
              color="rgba(255, 42, 42, 0.3)"
              top="10%"
              left="10%"
              animation={fogDrift2}
              duration="25s"
              delay="-3s"
              className="fog-cloud-red"
            />
            <StyledFogCloud
              size="500px"
              color="rgba(29, 78, 216, 0.3)"
              top="60%"
              left="60%"
              animation={fogDrift3}
              duration="35s"
              delay="-7s"
              className="fog-cloud-blue"
            />
          </StyledBackgroundWrapper>

          <StyledCursor
            active={isCursorActive}
            style={{
              transform: `translate3d(${cursorPos.x}px, ${cursorPos.y}px, 0) translate(-50%, -50%)`,
            }}
          />
          <a className="skip-to-content" href="#content">
            Skip to Content
          </a>
          {isLoading ? (
            <Loader
              finishLoading={() => {
                setIsLoading(false);
                hasLoadedInitially = true;
              }}
            />
          ) : (
            <StyledContent>
              <Nav isHome={isHome} />
              <Social isHome={isHome} />
              <Email isHome={isHome} />
              <div id="content">
                {children}
                <Footer />
              </div>
            </StyledContent>
          )}
        </ThemeProvider>
      </div>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
};

export default Layout;
